import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { Search, Building2, CheckCircle2, XCircle, Eye, Globe } from 'lucide-react';

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, [filter]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      
      const response = await axios.get(`${API}/admin/companies?${params.toString()}`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyStatus = async (companyId, newStatus, notes = null) => {
    setUpdating(true);
    try {
      const params = new URLSearchParams();
      params.append('status', newStatus);
      if (notes !== null) params.append('admin_notes', notes);
      
      await axios.put(`${API}/admin/companies/${companyId}?${params.toString()}`);
      toast.success(`Company ${newStatus}`);
      fetchCompanies();
      setSelectedCompany(null);
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setUpdating(false);
    }
  };

  const filteredCompanies = companies.filter(c => {
    if (search) {
      const searchLower = search.toLowerCase();
      return c.name?.toLowerCase().includes(searchLower) || 
             c.industry?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  const statusColors = {
    pending: 'badge-warning',
    approved: 'badge-success',
    rejected: 'badge-error',
  };

  const stageLabels = {
    seed: 'Seed',
    series_a: 'Series A',
    series_b: 'Series B',
    series_c: 'Series C',
    growth: 'Growth',
    public: 'Public',
  };

  return (
    <AdminLayout>
      <div data-testid="admin-companies-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">Manage Companies</h1>
            <p className="text-slate-600">{filteredCompanies.length} companies</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6" data-testid="company-filters">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name or industry..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Companies Table */}
        <Card className="overflow-hidden" data-testid="companies-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Industry</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Stage</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Size</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={6} className="px-6 py-4">
                        <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
                      </td>
                    </tr>
                  ))
                ) : filteredCompanies.length > 0 ? (
                  filteredCompanies.map((company) => (
                    <tr key={company.id} className="hover:bg-slate-50" data-testid={`company-row-${company.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-accent" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{company.name}</p>
                            <p className="text-sm text-slate-500">{company.contact_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{company.industry}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{stageLabels[company.stage] || company.stage}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{company.size}</td>
                      <td className="px-6 py-4">
                        <span className={`${statusColors[company.status]} text-xs`}>{company.status}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedCompany(company);
                              setAdminNotes(company.admin_notes || '');
                            }}
                            data-testid={`view-company-${company.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {company.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => updateCompanyStatus(company.id, 'approved')}
                                data-testid={`approve-company-${company.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => updateCompanyStatus(company.id, 'rejected')}
                                data-testid={`reject-company-${company.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      No companies found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Company Detail Modal */}
      <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="company-detail-modal">
          <DialogHeader>
            <DialogTitle>Company Details</DialogTitle>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedCompany.name}</h3>
                  <p className="text-sm text-slate-500">{selectedCompany.industry}</p>
                  <span className={`${statusColors[selectedCompany.status]} text-xs mt-1 inline-block`}>
                    {selectedCompany.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Stage</span>
                  <p className="font-medium">{stageLabels[selectedCompany.stage] || selectedCompany.stage}</p>
                </div>
                <div>
                  <span className="text-slate-500">Size</span>
                  <p className="font-medium">{selectedCompany.size}</p>
                </div>
                <div>
                  <span className="text-slate-500">Contact</span>
                  <p className="font-medium">{selectedCompany.contact_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Email</span>
                  <p className="font-medium">{selectedCompany.contact_email}</p>
                </div>
              </div>

              {selectedCompany.description && (
                <div>
                  <span className="text-sm text-slate-500">Description</span>
                  <p className="text-sm text-slate-700 mt-1">{selectedCompany.description}</p>
                </div>
              )}

              {selectedCompany.website_url && (
                <a href={selectedCompany.website_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full">
                    <Globe className="h-4 w-4 mr-2" />
                    Visit Website
                  </Button>
                </a>
              )}

              <div>
                <span className="text-sm text-slate-500">Admin Notes</span>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this company..."
                  className="mt-2"
                />
              </div>

              <div className="flex gap-2">
                {selectedCompany.status !== 'approved' && (
                  <Button
                    onClick={() => updateCompanyStatus(selectedCompany.id, 'approved', adminNotes)}
                    disabled={updating}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                )}
                {selectedCompany.status !== 'rejected' && (
                  <Button
                    variant="destructive"
                    onClick={() => updateCompanyStatus(selectedCompany.id, 'rejected', adminNotes)}
                    disabled={updating}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCompanies;
